import { Mail, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useLanguage } from "@/contexts/LanguageContext";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import Lottie from "lottie-react";
import highFiveAnimation from "@/assets/high-five.json";
import logo from "@/assets/khaled-logo.svg";

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();
  const { visibleItems, itemRefs } = useScrollAnimation(0.2);

  return (
    <footer className="relative border-t border-border mt-24 animate-fade-in overflow-hidden">
      {/* High Five Animation at Bottom Center */}
      <div
        aria-hidden="true"
        ref={(el) => (itemRefs.current[0] = el)}
        className={`absolute -bottom-12 md:-bottom-16 left-1/2 -translate-x-1/2 z-10 w-40 md:w-52 transition-all duration-700 ${
          visibleItems.has(0) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <Lottie animationData={highFiveAnimation} loop={true} />
      </div>
      
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12">
          {/* Column 1 - Branding */}
          <div className="md:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <img src={logo} alt="Khaled Kasmou" className="h-10 w-auto" />
            </Link>
            <p className="text-sm text-muted-foreground mb-2">
              {t("footer.tagline")}
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              {t("footer.location")}
            </p>
            <div className="flex items-center gap-2 mb-2">
              <div className="relative flex items-center justify-center">
                <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse"></div>
                <div className="absolute w-2.5 h-2.5 bg-green-500 rounded-full animate-ping"></div>
              </div>
              <span className="text-sm font-medium text-foreground">{t("footer.available")}</span>
            </div>
          </div>

          {/* Column 2 - Navigation */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">{t("footer.navigation")}</h4>
            <ul className="space-y-2.5">
              <li>
                <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  {t("footer.workLink")}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  {t("footer.aboutLink")}
                </Link>
              </li>
              <li>
                <Link to="/cv" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  {t("footer.cvLink")}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  {t("footer.contactLink")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3 - Services */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">{t("footer.expertise")}</h4>
            <ul className="space-y-2.5">
              <li className="text-sm text-muted-foreground">{t("footer.exp.uxui")}</li>
              <li className="text-sm text-muted-foreground">{t("footer.exp.ai")}</li>
              <li className="text-sm text-muted-foreground">{t("footer.exp.ds")}</li>
              <li className="text-sm text-muted-foreground">{t("footer.exp.research")}</li>
              <li className="text-sm text-muted-foreground">{t("footer.exp.leadership")}</li>
            </ul>
          </div>

          {/* Column 4 - Contact & Social */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">{t("footer.connect")}</h4>
            <div className="space-y-3 mb-4">
              <a
                href="mailto:khaledkasmou@gmail.com"
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group"
              >
                <Mail className="w-4 h-4 group-hover:scale-110 transition-transform" />
                {t("footer.email")}
              </a>
              <a
                href="https://www.linkedin.com/in/khaledkasmou/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group"
              >
                <Linkedin className="w-4 h-4 group-hover:scale-110 transition-transform" />
                {t("footer.linkedin")}
              </a>
            </div>
            <Button asChild size="sm" variant="premium" className="w-full">
              <Link to="/contact">{t("footer.cta")}</Link>
            </Button>
          </div>
        </div>

        {/* Bottom - Copyright and Language */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground text-center md:text-left">
            © {currentYear} Khaled Kasmou. {t("footer.copyright")}
          </p>
          <LanguageToggle />
        </div>
      </div>
    </footer>
  );
};
